import React, { useState, useEffect } from 'react';
import { FileText, Download, Check, Menu, X, Zap, Users, Shield, Star, Mail, Briefcase, Globe, Linkedin, Twitter, Github } from 'lucide-react';

const DocumentGenerator = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dailyCredits, setDailyCredits] = useState(3);
  const [documentType, setDocumentType] = useState('cv');
  const [formData, setFormData] = useState({
    // CV Fields
    fullName: '',
    email: '',
    phone: '',
    location: '',
    summary: '',
    experience: [{ title: '', company: '', duration: '', description: '' }],
    education: [{ degree: '', institution: '', year: '' }],
    skills: [],
    // Cover Letter Fields
    recipientName: '',
    companyName: '',
    position: '',
    yourName: '',
    yourEmail: '',
    letterBody: '',
    // Agreement Fields
    party1Name: '',
    party2Name: '',
    agreementDate: '',
    agreementTerms: '',
    paymentTerms: ''
  });

  useEffect(() => {
    // Check localStorage for user session
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    
    // Reset daily credits at midnight
    const lastReset = localStorage.getItem('lastCreditReset');
    const today = new Date().toDateString();
    if (lastReset !== today) {
      setDailyCredits(3);
      localStorage.setItem('lastCreditReset', today);
      localStorage.setItem('dailyCredits', '3');
    } else {
      const savedCredits = localStorage.getItem('dailyCredits');
      if (savedCredits) {
        setDailyCredits(parseInt(savedCredits));
      }
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    const mockUser = {
      id: Date.now(),
      email: e.target.email.value,
      plan: 'free',
      createdAt: new Date().toISOString()
    };
    setUser(mockUser);
    localStorage.setItem('user', JSON.stringify(mockUser));
    setActiveTab('dashboard');
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    setActiveTab('home');
  };

  const generateDocument = () => {
    if (dailyCredits <= 0 && (!user || user.plan === 'free')) {
      alert('You have no credits left today. Please upgrade to Pro for unlimited access!');
      return;
    }

    let content = '';
    
    if (documentType === 'cv') {
      content = `
CURRICULUM VITAE

${formData.fullName}
${formData.email} | ${formData.phone} | ${formData.location}

PROFESSIONAL SUMMARY
${formData.summary}

EXPERIENCE
${formData.experience.map(exp => `
${exp.title} at ${exp.company}
${exp.duration}
${exp.description}
`).join('\n')}

EDUCATION
${formData.education.map(edu => `
${edu.degree} - ${edu.institution} (${edu.year})
`).join('\n')}

SKILLS
${formData.skills.join(', ')}
      `;
    } else if (documentType === 'cover-letter') {
      content = `
${formData.yourName}
${formData.yourEmail}
${new Date().toLocaleDateString()}

${formData.recipientName}
${formData.companyName}

Dear ${formData.recipientName},

RE: Application for ${formData.position}

${formData.letterBody}

Sincerely,
${formData.yourName}
      `;
    } else if (documentType === 'agreement') {
      content = `
SERVICE AGREEMENT

This Agreement is entered into on ${formData.agreementDate} between:

Party 1: ${formData.party1Name}
Party 2: ${formData.party2Name}

TERMS OF AGREEMENT:
${formData.agreementTerms}

PAYMENT TERMS:
${formData.paymentTerms}

SIGNATURES:
_____________________          _____________________
${formData.party1Name}         ${formData.party2Name}
Date: _______________          Date: _______________
      `;
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${documentType}-${Date.now()}.txt`;
    a.click();

    if (!user || user.plan === 'free') {
      const newCredits = dailyCredits - 1;
      setDailyCredits(newCredits);
      localStorage.setItem('dailyCredits', newCredits.toString());
    }
  };

  const Navigation = () => (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-blue-600 mr-3" />
            <span className="text-xl font-bold text-gray-900">DocuGen Pro</span>
          </div>
          
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => setActiveTab('home')} className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">Home</button>
            <button onClick={() => setActiveTab('features')} className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">Features</button>
            <button onClick={() => setActiveTab('pricing')} className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">Pricing</button>
            <button onClick={() => setActiveTab('blog')} className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">Blog</button>
            {user ? (
              <>
                <button onClick={() => setActiveTab('dashboard')} className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">Dashboard</button>
                <button onClick={handleLogout} className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700">Logout</button>
              </>
            ) : (
              <button onClick={() => setActiveTab('login')} className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700">Login</button>
            )}
          </div>

          <div className="md:hidden flex items-center">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-700">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <button onClick={() => { setActiveTab('home'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">Home</button>
            <button onClick={() => { setActiveTab('features'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">Features</button>
            <button onClick={() => { setActiveTab('pricing'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">Pricing</button>
            <button onClick={() => { setActiveTab('blog'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">Blog</button>
            {user ? (
              <>
                <button onClick={() => { setActiveTab('dashboard'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100">Dashboard</button>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-red-600 hover:bg-gray-100">Logout</button>
              </>
            ) : (
              <button onClick={() => { setActiveTab('login'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-blue-600 hover:bg-gray-100">Login</button>
            )}
          </div>
        </div>
      )}
    </nav>
  );

  const HomePage = () => (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Create Professional Documents in Seconds
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Generate CVs, Cover Letters, and Agreements with AI-Powered Templates
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => user ? setActiveTab('dashboard') : setActiveTab('login')}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition"
              >
                Start Free (3 Documents/Day)
              </button>
              <button 
                onClick={() => setActiveTab('pricing')}
                className="bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-900 transition"
              >
                View Pro Plans
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="text-4xl font-bold text-blue-600 mb-2">50,000+</div>
            <div className="text-gray-600">Documents Generated</div>
          </div>
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="text-4xl font-bold text-blue-600 mb-2">98%</div>
            <div className="text-gray-600">Customer Satisfaction</div>
          </div>
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="text-4xl font-bold text-blue-600 mb-2">5 min</div>
            <div className="text-gray-600">Average Creation Time</div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Choose DocuGen Pro?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <Zap className="h-12 w-12 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Lightning Fast</h3>
            <p className="text-gray-600">Generate professional documents in seconds, not hours</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <Shield className="h-12 w-12 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Secure & Private</h3>
            <p className="text-gray-600">Your data is encrypted and never stored on our servers</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <Star className="h-12 w-12 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Professional Quality</h3>
            <p className="text-gray-600">Industry-standard templates used by top companies</p>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">What Our Users Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4">"DocuGen Pro saved me hours of work. I created a professional CV in minutes and landed my dream job!"</p>
              <p className="font-semibold">- Sarah Johnson</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4">"The agreement templates are perfect for my freelance business. Worth every penny!"</p>
              <p className="font-semibold">- Mike Chen</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const FeaturesPage = () => (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-center text-gray-900 mb-12">Features</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <FileText className="h-16 w-16 text-blue-600 mb-6" />
            <h2 className="text-2xl font-semibold mb-4">Multiple Document Types</h2>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Professional CVs with modern layouts</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Compelling cover letters that get noticed</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Legal agreements and contracts</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Business proposals and invoices</span>
              </li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg">
            <Zap className="h-16 w-16 text-blue-600 mb-6" />
            <h2 className="text-2xl font-semibold mb-4">Smart Features</h2>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>AI-powered content suggestions</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Industry-specific templates</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Real-time preview and editing</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Export to PDF, Word, or plain text</span>
              </li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg">
            <Shield className="h-16 w-16 text-blue-600 mb-6" />
            <h2 className="text-2xl font-semibold mb-4">Security & Privacy</h2>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>No data storage - everything stays on your device</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>SSL encryption for all connections</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>GDPR compliant</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Anonymous usage available</span>
              </li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg">
            <Users className="h-16 w-16 text-blue-600 mb-6" />
            <h2 className="text-2xl font-semibold mb-4">Pro Benefits</h2>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Unlimited document generation</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Priority support</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Custom branding options</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                <span>Advanced templates and layouts</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const PricingPage = () => (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-center text-gray-900 mb-4">Simple, Transparent Pricing</h1>
        <p className="text-xl text-center text-gray-600 mb-12">Choose the plan that works for you</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gray-100 px-6 py-8 text-center">
              <h2 className="text-2xl font-semibold text-gray-900">Free</h2>
              <div className="mt-4">
                <span className="text-4xl font-bold">$0</span>
                <span className="text-gray-600">/month</span>
              </div>
            </div>
            <div className="px-6 py-8">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>3 documents per day</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>Basic templates</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>Text export only</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-gray-400 mr-2 mt-1" />
                  <span className="text-gray-500">Priority support</span>
                </li>
              </ul>
              <button 
                onClick={() => user ? setActiveTab('dashboard') : setActiveTab('login')}
                className="w-full mt-8 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
              >
                Get Started
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-blue-600">
            <div className="bg-blue-600 px-6 py-8 text-center">
              <h2 className="text-2xl font-semibold text-white">Pro</h2>
              <div className="mt-4">
                <span className="text-4xl font-bold text-white">$9</span>
                <span className="text-blue-200">/month</span>
              </div>
              <p className="text-blue-200 mt-2">Most Popular</p>
            </div>
            <div className="px-6 py-8">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>Unlimited documents</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>Premium templates</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>PDF & Word export</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>Priority support</span>
                </li>
              </ul>
              <button className="w-full mt-8 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition">
                Upgrade to Pro
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gray-900 px-6 py-8 text-center">
              <h2 className="text-2xl font-semibold text-white">Enterprise</h2>
              <div className="mt-4">
                <span className="text-4xl font-bold text-white">Custom</span>
              </div>
            </div>
            <div className="px-6 py-8">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>Everything in Pro</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>Custom branding</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>API access</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 mt-1" />
                  <span>Dedicated account manager</span>
                </li>
              </ul>
              <button className="w-full mt-8 bg-gray-900 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition">
                Contact Sales
              </button>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <h3 className="text-2xl font-semibold mb-4">Frequently Asked Questions</h3>
          <div className="max-w-3xl mx-auto text-left space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h4 className="font-semibold mb-2">Can I cancel anytime?</h4>
              <p className="text-gray-600">Yes, you can cancel your subscription at any time. No questions asked.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h4 className="font-semibold mb-2">Do you offer refunds?</h4>
              <p className="text-gray-600">We offer a 30-day money-back guarantee for all paid plans.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h4 className="font-semibold mb-2">Is my data secure?</h4>
              <p className="text-gray-600">Absolutely. We don't store any of your document data. Everything is processed on your device.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const BlogPage = () => (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-center text-gray-900 mb-12">Blog & Resources</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { color: 'from-blue-400 to-blue-600', title: 'How to Write a Winning CV in 2025', desc: 'Learn the latest CV trends and tips to make your resume stand out from the crowd.' },
            { color: 'from-green-400 to-green-600', title: 'Cover Letter Templates That Get Interviews', desc: 'Discover proven cover letter formats that hiring managers love.' },
            { color: 'from-purple-400 to-purple-600', title: 'Essential Clauses for Service Agreements', desc: 'Protect your business with these must-have contract clauses.' },
            { color: 'from-red-400 to-red-600', title: '10 Common CV Mistakes to Avoid', desc: "Don't let these errors cost you your dream job opportunity." },
            { color: 'from-yellow-400 to-yellow-600', title: 'ATS-Friendly Resume Formatting Guide', desc: 'Ensure your CV passes through applicant tracking systems.' },
            { color: 'from-indigo-400 to-indigo-600', title: 'Legal Document Best Practices', desc: 'Essential tips for creating legally sound business documents.' },
          ].map((post, i) => (
            <article key={i} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className={`h-48 bg-gradient-to-r ${post.color}`}></div>
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-2">{post.title}</h2>
                <p className="text-gray-600 mb-4">{post.desc}</p>
                <button
                  onClick={() => {}}
                  className="text-blue-600 font-semibold hover:text-blue-700"
                >
                  Read More →
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );

  const LoginPage = () => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-blue-600" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Sign in to DocuGen Pro</h2>
          <p className="mt-2 text-sm text-gray-600">
            Or{' '}
            <button
              onClick={() => setActiveTab('pricing')}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              start your free trial
            </button>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">Email address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <button
                type="button"
                onClick={() => {}}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Sign in
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button type="button" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="ml-2">Google</span>
              </button>

              <button type="button" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                <Github className="w-5 h-5" />
                <span className="ml-2">GitHub</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  const DashboardPage = () => (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Document Generator Dashboard</h1>
            <div className="text-right">
              <p className="text-sm text-gray-600">Daily Credits Remaining</p>
              <p className="text-3xl font-bold text-blue-600">{user && user.plan === 'pro' ? '∞' : dailyCredits}/3</p>
            </div>
          </div>

          {user && user.plan === 'free' && dailyCredits === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Zap className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Out of daily credits</h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    Upgrade to Pro for unlimited document generation!
                  </p>
                  <button onClick={() => setActiveTab('pricing')} className="mt-2 text-sm font-medium text-yellow-800 underline">
                    View pricing →
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Document Type</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setDocumentType('cv')}
                className={`p-4 border-2 rounded-lg text-center transition ${
                  documentType === 'cv' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Briefcase className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                <span className="font-medium">CV/Resume</span>
              </button>
              <button
                onClick={() => setDocumentType('cover-letter')}
                className={`p-4 border-2 rounded-lg text-center transition ${
                  documentType === 'cover-letter' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Mail className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                <span className="font-medium">Cover Letter</span>
              </button>
              <button
                onClick={() => setDocumentType('agreement')}
                className={`p-4 border-2 rounded-lg text-center transition ${
                  documentType === 'agreement' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <FileText className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                <span className="font-medium">Agreement</span>
              </button>
            </div>
          </div>

          {documentType === 'cv' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="San Francisco, CA"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Professional Summary</label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({...formData, summary: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief summary of your professional background and key strengths..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
                <div className="space-y-4">
                  {formData.experience.map((exp, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Job Title"
                          value={exp.title}
                          onChange={(e) => {
                            const newExp = [...formData.experience];
                            newExp[index].title = e.target.value;
                            setFormData({...formData, experience: newExp});
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="Company"
                          value={exp.company}
                          onChange={(e) => {
                            const newExp = [...formData.experience];
                            newExp[index].company = e.target.value;
                            setFormData({...formData, experience: newExp});
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Duration (e.g., 2020-2023)"
                        value={exp.duration}
                        onChange={(e) => {
                          const newExp = [...formData.experience];
                          newExp[index].duration = e.target.value;
                          setFormData({...formData, experience: newExp});
                        }}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <textarea
                        placeholder="Job description and achievements..."
                        value={exp.description}
                        onChange={(e) => {
                          const newExp = [...formData.experience];
                          newExp[index].description = e.target.value;
                          setFormData({...formData, experience: newExp});
                        }}
                        rows={2}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  ))}
                  <button
                    onClick={() => setFormData({
                      ...formData,
                      experience: [...formData.experience, { title: '', company: '', duration: '', description: '' }]
                    })}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    + Add Experience
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Skills</label>
                <input
                  type="text"
                  placeholder="Enter skills separated by commas"
                  value={formData.skills.join(', ')}
                  onChange={(e) => setFormData({...formData, skills: e.target.value.split(',').map(s => s.trim())})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          {documentType === 'cover-letter' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                  <input
                    type="text"
                    value={formData.yourName}
                    onChange={(e) => setFormData({...formData, yourName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Email</label>
                  <input
                    type="email"
                    value={formData.yourEmail}
                    onChange={(e) => setFormData({...formData, yourEmail: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Name</label>
                  <input
                    type="text"
                    value={formData.recipientName}
                    onChange={(e) => setFormData({...formData, recipientName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Hiring Manager"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({...formData, position: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Letter Body</label>
                <textarea
                  value={formData.letterBody}
                  onChange={(e) => setFormData({...formData, letterBody: e.target.value})}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Write your cover letter content here..."
                />
              </div>
            </div>
          )}

          {documentType === 'agreement' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Party 1 Name</label>
                  <input
                    type="text"
                    value={formData.party1Name}
                    onChange={(e) => setFormData({...formData, party1Name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Party 2 Name</label>
                  <input
                    type="text"
                    value={formData.party2Name}
                    onChange={(e) => setFormData({...formData, party2Name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agreement Date</label>
                <input
                  type="date"
                  value={formData.agreementDate}
                  onChange={(e) => setFormData({...formData, agreementDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agreement Terms</label>
                <textarea
                  value={formData.agreementTerms}
                  onChange={(e) => setFormData({...formData, agreementTerms: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the terms of the agreement..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                <textarea
                  value={formData.paymentTerms}
                  onChange={(e) => setFormData({...formData, paymentTerms: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Specify payment amounts, schedule, and method..."
                />
              </div>
            </div>
          )}

          <div className="flex justify-center mt-8">
            <button
              onClick={generateDocument}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition flex items-center"
            >
              <Download className="h-5 w-5 mr-2" />
              Generate Document
            </button>
          </div>
        </div>

        {/* Affiliate Section */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Recommended Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="https://grammarly.com" target="_blank" rel="noopener noreferrer" className="border border-gray-200 rounded-lg p-4 hover:border-blue-600 transition">
              <h3 className="font-medium mb-2">Grammarly</h3>
              <p className="text-sm text-gray-600">Perfect your documents with AI-powered writing assistance</p>
            </a>
            <a href="https://canva.com" target="_blank" rel="noopener noreferrer" className="border border-gray-200 rounded-lg p-4 hover:border-blue-600 transition">
              <h3 className="font-medium mb-2">Canva</h3>
              <p className="text-sm text-gray-600">Create stunning visual resumes and portfolios</p>
            </a>
            <a href="https://linkedin.com/premium" target="_blank" rel="noopener noreferrer" className="border border-gray-200 rounded-lg p-4 hover:border-blue-600 transition">
              <h3 className="font-medium mb-2">LinkedIn Premium</h3>
              <p className="text-sm text-gray-600">Get insights and stand out to recruiters</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  const Footer = () => (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center mb-4">
              <FileText className="h-8 w-8 text-blue-400 mr-3" />
              <span className="text-xl font-bold">DocuGen Pro</span>
            </div>
            <p className="text-gray-400">Professional document generation made simple</p>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Product</h3>
            <ul className="space-y-2">
              <li><button onClick={() => setActiveTab('features')} className="text-gray-400 hover:text-white">Features</button></li>
              <li><button onClick={() => setActiveTab('pricing')} className="text-gray-400 hover:text-white">Pricing</button></li>
              <li><button onClick={() => setActiveTab('dashboard')} className="text-gray-400 hover:text-white">Templates</button></li>
              <li><button onClick={() => {}} className="text-gray-400 hover:text-white">API</button></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              <li><button onClick={() => {}} className="text-gray-400 hover:text-white">About</button></li>
              <li><button onClick={() => setActiveTab('blog')} className="text-gray-400 hover:text-white">Blog</button></li>
              <li><button onClick={() => {}} className="text-gray-400 hover:text-white">Careers</button></li>
              <li><button onClick={() => {}} className="text-gray-400 hover:text-white">Contact</button></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li><button onClick={() => {}} className="text-gray-400 hover:text-white">Privacy Policy</button></li>
              <li><button onClick={() => {}} className="text-gray-400 hover:text-white">Terms of Service</button></li>
              <li><button onClick={() => {}} className="text-gray-400 hover:text-white">Cookie Policy</button></li>
              <li><button onClick={() => {}} className="text-gray-400 hover:text-white">GDPR</button></li>
            </ul>
          </div>
        </div>
        
        <div className="mt-8 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400">&copy; 2025 DocuGen Pro. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                <Twitter className="h-6 w-6" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                <Linkedin className="h-6 w-6" />
              </a>
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                <Github className="h-6 w-6" />
              </a>
              <a href="https://docugenpro.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                <Globe className="h-6 w-6" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      {activeTab === 'home' && <HomePage />}
      {activeTab === 'features' && <FeaturesPage />}
      {activeTab === 'pricing' && <PricingPage />}
      {activeTab === 'blog' && <BlogPage />}
      {activeTab === 'login' && <LoginPage />}
      {activeTab === 'dashboard' && <DashboardPage />}
      
      <Footer />
    </div>
  );
};

export default DocumentGenerator;
